import React, { useState, useEffect } from "react";

import type { TUser, TClient } from "@/types"; // Adjust the import path as necessary

import { FeedbackText } from "./FeedbackText";

import "./AddClient.css";

type AddClientProps = {
  apiClient: any; // Replace with your actual ApiClient type if available
  onClientAdded?: (client: TClient) => void;
  onClose?: () => void;
};

const isValidDomain = (domain: string) => {
  // Simple domain validation (e.g. example.com)
  return /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain);
};

const AddClient: React.FC<AddClientProps> = ({
  apiClient,
  onClientAdded,
  onClose,
}) => {
  const [client, setClient] = useState<TClient>({
    domain: "",
    name: "",
    accountManagerEmail: "",
  });
  const [accountManagers, setAccountManagers] = useState<TUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAccountManagers = async () => {
      try {
        const managers = await apiClient.getAccountManagers();
        setAccountManagers(managers || []);
      } catch {
        setAccountManagers([]);
      }
    };
    fetchAccountManagers();
  }, [apiClient]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setClient({ ...client, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidDomain(client.domain)) {
      setError("Please enter a valid domain (e.g. example.com)");
      return;
    }
    if (!client.name || !client.accountManagerEmail) {
      setError("Please fill in all fields.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.upsertClient(client);
      if (onClientAdded) onClientAdded(client);
      if (onClose) onClose();
    } catch (err) {
      setError("Failed to add client.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-client-modal">
      <form className="add-client-form" onSubmit={handleSubmit}>
        <label htmlFor="name">Client Name:</label>
        <input
          id="name"
          type="text"
          name="name"
          value={client.name}
          onChange={handleChange}
          required
        />
        <label htmlFor="domain">Domain:</label>
        <input
          id="domain"
          type="text"
          name="domain"
          value={client.domain}
          onChange={handleChange}
          placeholder="example.com"
          required
        />
        <label htmlFor="accountManagerEmail">Account Manager:</label>
        <select
          id="accountManagerEmail"
          name="accountManagerEmail"
          value={client.accountManagerEmail}
          onChange={handleChange}
          required
        >
          <option value="">Select an account manager</option>
          {accountManagers.map((user: TUser) => (
            <option key={user.email} value={user.email}>
              {user.email || ""}
            </option>
          ))}
        </select>

        {error && <FeedbackText message={error} />}
        <div className="form-actions">
          {onClose && (
            <button type="button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          )}

          <button type="submit" className="save-btn" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddClient;
